// Onglet « Vérification du bailleur » (F-008) et espace d'examen de l'équipe Bailivo.
import { useState } from "react";
import { del, post, put, upload } from "@core/api";
import { date, dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { PublicShell } from "@core/layout";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, FileLine, Input, Loading, Notice, PageHeader, Select, StatusChip, Table, Textarea, fieldError, useToast } from "@core/ui";
import { VerifChip } from "./shared";

type Verification = {
  id: string | null;
  declared: boolean;
  relation: string | null;
  relation_label: string | null;
  declared_holder: string | null;
  status: string;
  status_label: string;
  explanation: string;
  scope: { code: string; label: string }[];
  not_verified: string[];
  submitted_at: string | null;
  reviewed_at: string | null;
  requirements: { code: string; label: string; required: boolean; satisfied: boolean; kinds: { kind: string; label: string }[] }[];
  missing: string[];
  allowed_kinds: { kind: string; label: string }[];
  documents: { id: string; kind: string; kind_label: string; filename: string; size_bytes: number; url: string; created_at: string }[];
  relations: { value: string; label: string }[];
  notice: string;
  events: { action: string; status: string; explanation: string; actor_kind: string; actor_name: string | null; created_at: string }[];
  can_edit?: boolean;
};

const ACTIONS: Record<string, string> = {
  declaration: "Lien déclaré",
  piece_deposee: "Pièce déposée",
  piece_retiree: "Pièce retirée",
  soumission: "Examen demandé",
  examen: "Examen de l’équipe",
  reinitialisation: "Nouvel examen nécessaire",
};

function History({ events }: { events: Verification["events"] }) {
  if (!events.length) return null;
  return (
    <Card title="Historique">
      <Table caption="Historique de la vérification">
        <thead>
          <tr>
            <th>Date</th>
            <th>Étape</th>
            <th>Par</th>
            <th>Détail</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={i}>
              <td className="small">{dateTime(e.created_at)}</td>
              <td>{ACTIONS[e.action] ?? e.action}</td>
              <td className="small">{e.actor_name ?? (e.actor_kind === "equipe" ? "Équipe Bailivo" : "Bailleur")}</td>
              <td className="small">{e.explanation}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

function Scope({ v }: { v: Verification }) {
  return (
    <div className="grid-2">
      <div className="stack-sm">
        <h3>Ce qui a été vérifié</h3>
        {v.scope.length ? (
          <ul className="pat-list">
            {v.scope.map((s) => (
              <li key={s.code}>{s.label}</li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Rien pour le moment : aucun statut n’est posé automatiquement.</p>
        )}
      </div>
      <div className="stack-sm">
        <h3>Ce qui n’est pas vérifié</h3>
        <ul className="pat-list small">
          {v.not_verified.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function VerificationTab({ propertyId, canEdit }: { propertyId: string; canEdit: boolean }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const url = org.api(`/properties/${propertyId}/landlord-verification`);
  const q = useGet<Verification>(url);
  const [form, setForm] = useState<{ relation: string; declared_holder: string } | null>(null);
  const [kind, setKind] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const v = q.data;
  const refresh = () => invalidate(org.api(`/properties/${propertyId}`), org.api("/properties"), org.api("/patrimoine"));
  const editing = form ?? (!v.declared && canEdit ? { relation: "proprietaire", declared_holder: "" } : null);
  const locked = v.status === "a_confirmer";
  const chosenKind = v.allowed_kinds.some((k) => k.kind === kind) ? kind : (v.allowed_kinds[0]?.kind ?? "");
  return (
    <div className="stack">
      <Notice>{v.notice}</Notice>
      <Card title="Statut">
        <div className="row">
          <VerifChip status={v.status} />
          {v.reviewed_at ? <span className="small muted">Examen du {date(v.reviewed_at)}</span> : v.submitted_at ? <span className="small muted">Demandé le {date(v.submitted_at)}</span> : null}
        </div>
        {v.explanation ? <p>{v.explanation}</p> : null}
        {v.declared ? (
          <p className="small">
            Lien déclaré : <strong>{v.relation_label}</strong> · propriétaire tel qu’il figure sur les pièces : <strong>{v.declared_holder}</strong>
          </p>
        ) : null}
        <Scope v={v} />
      </Card>
      {editing && canEdit ? (
        <Card title="Votre lien avec ce logement">
          <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
          <div className="form-row">
            <Select label="Vous êtes" value={editing.relation} options={v.relations} error={fieldError(error, "relation")} onChange={(e) => setForm({ ...editing, relation: e.target.value })} />
            <Input
              label="Propriétaire tel qu’il figure sur les pièces"
              value={editing.declared_holder}
              error={fieldError(error, "declared_holder")}
              onChange={(e) => setForm({ ...editing, declared_holder: e.target.value })}
            />
          </div>
          {v.status === "verifie_equipe" ? <Notice tone="attention">Modifier ces informations annule la vérification : un nouvel examen sera nécessaire.</Notice> : null}
          <div className="form-actions">
            {v.declared ? (
              <Button variant="discret" onClick={() => setForm(null)}>
                Annuler
              </Button>
            ) : null}
            <Button
              busy={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await put(url, editing);
                  setForm(null);
                  refresh();
                  toast("Lien enregistré.");
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Enregistrer
            </Button>
          </div>
        </Card>
      ) : null}
      {v.declared ? (
        <Card
          title="Pièces justificatives"
          actions={
            canEdit && !form ? (
              <Button small variant="discret" onClick={() => setForm({ relation: v.relation ?? "proprietaire", declared_holder: v.declared_holder ?? "" })}>
                Modifier le lien déclaré
              </Button>
            ) : null
          }
        >
          <ul className="list-plain">
            {v.requirements.map((r) => (
              <li key={r.code} className="row">
                <StatusChip status={r.satisfied ? "conforme" : r.required ? "manquant" : "info"} label={r.satisfied ? "Fournie" : r.required ? "Pièce manquante" : "Facultative"} />
                <span>
                  {r.label} <span className="muted small">({r.kinds.map((k) => k.label).join(", ")})</span>
                </span>
              </li>
            ))}
          </ul>
          {v.documents.map((d) => (
            <FileLine
              key={d.id}
              name={`${d.kind_label} — ${d.filename}`}
              size={d.size_bytes}
              href={d.url}
              right={
                canEdit ? (
                  <Button
                    small
                    variant="discret"
                    onClick={async () => {
                      try {
                        await del(`${url}/documents/${d.id}`);
                        refresh();
                        toast("Pièce retirée et effacée.");
                      } catch (e: any) {
                        toast(e.message, "erreur");
                      }
                    }}
                  >
                    Retirer
                  </Button>
                ) : null
              }
            />
          ))}
          {canEdit && !locked ? (
            <div className="stack-sm">
              <p className="small muted">Seules les pièces utiles au lien déclaré sont acceptées. Aucune pièce d’identité ni aucun relevé bancaire n’est demandé ici.</p>
              <Select label="Pièce déposée" value={chosenKind} options={v.allowed_kinds.map((k) => ({ value: k.kind, label: k.label }))} onChange={(e) => setKind(e.target.value)} />
              <FileDrop
                busy={busy}
                onFiles={async (files) => {
                  if (!files[0]) return;
                  setBusy(true);
                  try {
                    const f = new FormData();
                    f.append("kind", chosenKind);
                    f.append("file", files[0]);
                    await upload(`${url}/documents`, f);
                    refresh();
                    toast("Pièce déposée.");
                  } catch (e: any) {
                    toast(e.message, "erreur");
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </div>
          ) : null}
          {canEdit && (v.status === "non_verifie" || v.status === "refuse") ? (
            <div className="form-actions">
              <Button
                disabled={v.missing.length > 0}
                onClick={async () => {
                  try {
                    await post(`${url}/submit`);
                    refresh();
                    toast("Examen demandé à l’équipe Bailivo.");
                  } catch (e: any) {
                    toast(e.message, "erreur");
                  }
                }}
              >
                Demander l’examen par l’équipe
              </Button>
            </div>
          ) : null}
          {v.missing.length && canEdit ? <p className="small muted">Avant de demander l’examen : {v.missing.join(" ; ")}.</p> : null}
        </Card>
      ) : !canEdit ? (
        <Empty title="Aucun lien déclaré pour ce logement." />
      ) : null}
      <History events={v.events} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Espace d'examen de l'équipe Bailivo (/moi/equipe/verifications-bailleurs)

type QueueItem = { id: string; org_name: string; property_label: string; property_address: string; relation_label: string; declared_holder: string; status: string; submitted_at: string | null; pieces: number };

type TeamDetail = Verification & {
  org_name: string;
  property: { label: string; kind_label: string; address_line: string };
  declared_owners: { display_name: string; kind: string }[];
  scope_items: { code: string; label: string }[];
  conflict_of_interest: boolean;
};

export function TeamReviewPage() {
  const [status, setStatus] = useState("a_confirmer");
  const [selected, setSelected] = useState<string | null>(null);
  const q = useGet<{ items: QueueItem[] }>(`/api/v1/equipe/landlord-verifications?status=${status}`);
  const notTeam = (q.error as any)?.status === 404;
  return (
    <PublicShell wide>
      <div className="page">
        <PageHeader eyebrow="Équipe Bailivo" title="Vérification des bailleurs" intro="Examen humain des pièces déposées. Indiquez exactement ce que vous avez vérifié ; rien n’est validé automatiquement." />
        {notTeam ? (
          <Notice tone="attention">Cette page est réservée à l’équipe de vérification de Bailivo.</Notice>
        ) : (
          <>
            <Select
              label="Afficher"
              value={status}
              options={[
                { value: "a_confirmer", label: "À examiner" },
                { value: "verifie_equipe", label: "Vérifiés" },
                { value: "refuse", label: "Refusés" },
                { value: "tous", label: "Tous" },
              ]}
              onChange={(e) => {
                setStatus(e.target.value);
                setSelected(null);
              }}
            />
            {q.isLoading ? <Loading /> : null}
            <ErrorBox error={notTeam ? null : q.error} />
            {q.data?.items.length === 0 ? <Empty title="Aucune demande dans cette liste." /> : null}
            {q.data?.items.length ? (
              <Table caption="Demandes de vérification">
                <thead>
                  <tr>
                    <th>Logement</th>
                    <th>Espace</th>
                    <th>Lien déclaré</th>
                    <th>Demandé le</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.items.map((i) => (
                    <tr key={i.id}>
                      <td>
                        <Button small variant="discret" onClick={() => setSelected(i.id)}>
                          {i.property_label}
                        </Button>
                        <span className="pat-sub">{i.property_address}</span>
                      </td>
                      <td className="small">{i.org_name}</td>
                      <td className="small">
                        {i.relation_label} · {i.declared_holder} · {i.pieces} pièce{i.pieces > 1 ? "s" : ""}
                      </td>
                      <td className="small">{i.submitted_at ? dateTime(i.submitted_at) : "—"}</td>
                      <td>
                        <VerifChip status={i.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : null}
            {selected ? <TeamReview id={selected} onDone={() => setSelected(null)} /> : null}
          </>
        )}
      </div>
    </PublicShell>
  );
}

function TeamReview({ id, onDone }: { id: string; onDone: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<TeamDetail>(`/api/v1/equipe/landlord-verifications/${id}`);
  const [status, setStatus] = useState("verifie_equipe");
  const [scope, setScope] = useState<string[]>([]);
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <Card title={`${d.property.label} — ${d.org_name}`}>
      <p className="small">
        {d.property.kind_label} · {d.property.address_line}
      </p>
      <p>
        Lien déclaré : <strong>{d.relation_label}</strong> · propriétaire indiqué sur les pièces : <strong>{d.declared_holder}</strong>
      </p>
      <p className="small">
        Propriétaires saisis dans l’espace : {d.declared_owners.length ? d.declared_owners.map((o) => o.display_name).join(", ") : "aucun"}
      </p>
      <div className="stack-sm">
        <h3>Pièces (consultation journalisée)</h3>
        {d.documents.map((doc) => (
          <FileLine key={doc.id} name={`${doc.kind_label} — ${doc.filename}`} size={doc.size_bytes} href={doc.url} />
        ))}
        {d.missing.length ? <Notice tone="attention">Pièce manquante : {d.missing.join(" ; ")}</Notice> : null}
      </div>
      {d.conflict_of_interest ? (
        <Notice tone="attention">Vous êtes membre de cet espace : un autre membre de l’équipe doit examiner ce dossier.</Notice>
      ) : (
        <div className="stack">
          <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
          <fieldset className="pat-fieldset">
            <legend>Décision</legend>
            {[
              { value: "verifie_equipe", label: "Lien vérifié sur pièce" },
              { value: "a_confirmer", label: "Complément demandé au bailleur" },
              { value: "refuse", label: "Lien non établi (refus)" },
            ].map((o) => (
              <label key={o.value} className="pat-radio">
                <input type="radio" name="decision" value={o.value} checked={status === o.value} onChange={() => setStatus(o.value)} />
                <span>{o.label}</span>
              </label>
            ))}
            {fieldError(error, "status") ? <p className="bv-champ-aide">{fieldError(error, "status")}</p> : null}
          </fieldset>
          {status === "verifie_equipe" ? (
            <fieldset className="pat-fieldset">
              <legend>Ce que vous avez réellement vérifié</legend>
              {d.scope_items.map((s) => (
                <Checkbox key={s.code} label={s.label} checked={scope.includes(s.code)} onChange={(c) => setScope(c ? [...scope, s.code] : scope.filter((x) => x !== s.code))} />
              ))}
              {fieldError(error, "scope") ? <p className="bv-champ-aide">{fieldError(error, "scope")}</p> : null}
            </fieldset>
          ) : null}
          <Textarea
            label={status === "verifie_equipe" ? "Commentaire (facultatif)" : "Explication pour le bailleur"}
            value={explanation}
            error={fieldError(error, "explanation")}
            onChange={(e) => setExplanation(e.target.value)}
          />
          <div className="form-actions">
            <Button variant="discret" onClick={onDone}>
              Fermer
            </Button>
            <Button
              busy={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await post(`/api/v1/equipe/landlord-verifications/${id}/review`, { status, scope: status === "verifie_equipe" ? scope : [], explanation });
                  invalidate("/api/v1/equipe/");
                  toast("Examen enregistré. Le bailleur est informé.");
                  onDone();
                } catch (e) {
                  setError(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Enregistrer l’examen
            </Button>
          </div>
        </div>
      )}
      <History events={d.events} />
      <Badge>Examen humain : aucune décision automatique</Badge>
    </Card>
  );
}
